/**
 * Menu - Main and pause menu UI
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export type MenuType = 'main' | 'pause';

interface MenuProps {
  isOpen: boolean;
  type: MenuType;
  onClose: () => void;
  onNewGame?: () => void;
  onContinue?: () => void;
  onSettings: () => void;
  onChapters?: () => void;
  onQuit?: () => void;
}

const Menu: React.FC<MenuProps> = ({
  isOpen,
  type,
  onClose,
  onNewGame,
  onContinue,
  onSettings,
  onChapters,
  onQuit,
}) => {
  // Menu button component
  const MenuButton: React.FC<{
    onClick: () => void;
    children: React.ReactNode;
    variant?: 'primary' | 'secondary' | 'danger';
  }> = ({ onClick, children, variant = 'primary' }) => {
    const baseClasses =
      'w-full px-4 sm:px-6 py-2.5 sm:py-3 rounded font-medium text-base sm:text-lg transition-all';
    const variantClasses = {
      primary: 'bg-white text-black hover:bg-gray-200',
      secondary: 'bg-white/10 text-white hover:bg-white/20 border border-white/20',
      danger: 'bg-white/10 text-white hover:bg-red-900/30 border border-white/20',
    };

    return (
      <button
        onClick={onClick}
        className={`${baseClasses} ${variantClasses[variant]}`}
      >
        {children}
      </button>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/80 z-50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={type === 'pause' ? onClose : undefined}
          />

          {/* Menu Panel */}
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-black/95 backdrop-blur-md rounded border border-white/10 shadow-2xl w-full max-w-md overflow-hidden pointer-events-auto"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-4 sm:px-6 py-6 sm:py-8 text-center border-b border-white/10">
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 tracking-wide">NordicNeve</h1>
                <p className="text-gray-400 text-xs sm:text-sm">
                  {type === 'main' ? 'Interactive Story Experience' : 'Game Paused'}
                </p>
              </div>

              {/* Menu Content */}
              <div className="p-4 sm:p-6 space-y-2.5 sm:space-y-3">
                {/* Main Menu Options */}
                {type === 'main' && (
                  <>
                    {onNewGame && (
                      <MenuButton onClick={onNewGame} variant="primary">
                        New Game
                      </MenuButton>
                    )}
                    {onContinue && (
                      <MenuButton onClick={onContinue} variant="primary">
                        Continue
                      </MenuButton>
                    )}
                  </>
                )}

                {/* Pause Menu Options */}
                {type === 'pause' && (
                  <>
                    <MenuButton onClick={onClose} variant="primary">
                      Resume
                    </MenuButton>
                  </>
                )}

                {/* Common Options */}
                <MenuButton onClick={onSettings} variant="secondary">
                  Settings
                </MenuButton>

                {onChapters && (
                  <MenuButton onClick={onChapters} variant="secondary">
                    Chapters
                  </MenuButton>
                )}

                {onQuit && (
                  <MenuButton onClick={onQuit} variant="danger">
                    {type === 'main' ? 'Quit' : 'Return to Main Menu'}
                  </MenuButton>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 text-center border-t border-white/10 hidden sm:block">
                <p className="text-gray-500 text-xs">
                  Press ESC to {type === 'pause' ? 'resume' : 'close'}
                </p>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Menu;
